/* Modal del Comunicado institucional (imagen a pantalla completa).
 *
 * Comportamiento (espejo del MicroEncuestaModal):
 * - Se muestra una sola vez por persona, al iniciar sesión, antes del Home.
 * - Solo en PWA instalada o celular. En el navegador de escritorio no aparece.
 * - Se cierra solo a los 40 segundos, o antes con la X / tocando la pantalla.
 *
 * La marca de "ya visto" se guarda en localStorage con la cédula del usuario,
 * y se escribe apenas se muestra (no al cerrarse) para que no se repita aunque
 * la app se cierre de golpe.
 *
 * Para publicar un comunicado nuevo: cambiar COMUNICADO_ID (así vuelve a
 * mostrarse a todos) y la imagen de COMUNICADO_IMG.
 */
import { IonModal } from "@ionic/react";
import { useCallback, useEffect, useRef, useState } from "react";
import "./ComunicadoModal.css";

const COMUNICADO_ID = "responsabilidad";
const COMUNICADO_IMG = "assets/images/comunicado-responsabilidad.jpeg";
const SEGUNDOS_VISIBLE = 40;

const claveVisto = (cedula: string) => `comunicado:${COMUNICADO_ID}:${cedula}`;

// PWA instalada (standalone) o cualquier celular. Se oculta en escritorio.
const esDispositivo = (): boolean => {
  try {
    const standalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (window.navigator as any).standalone === true;
    const ua = navigator.userAgent || navigator.vendor || "";
    const esMovil = /Android|iPhone|iPad|iPod|IEMobile|BlackBerry|Opera Mini|Mobile/i.test(ua);
    return !!(standalone || esMovil);
  } catch (e) {
    return false;
  }
};

interface Props {
  isAuth: boolean;
  cedula: string;
}

const ComunicadoModal: React.FC<Props> = ({ isAuth, cedula }) => {
  const [abierto, setAbierto] = useState(false);
  const [restante, setRestante] = useState(SEGUNDOS_VISIBLE);

  const yaVerificado = useRef(false);

  // Verificación inicial al autenticarse (una sola vez por sesión).
  // Espera a que llegue la cédula, que se carga junto con el perfil.
  useEffect(() => {
    if (!isAuth) {
      yaVerificado.current = false;
      setAbierto(false);
      return;
    }
    if (yaVerificado.current || !cedula) return;
    yaVerificado.current = true;

    if (!esDispositivo()) return;

    try {
      if (localStorage.getItem(claveVisto(cedula))) return;
      localStorage.setItem(claveVisto(cedula), new Date().toISOString());
    } catch (e) {
      // Si el navegador bloquea el almacenamiento, se muestra igual una vez por sesión.
    }

    setRestante(SEGUNDOS_VISIBLE);
    setAbierto(true);
  }, [isAuth, cedula]);

  const cerrar = useCallback(() => setAbierto(false), []);

  // Cuenta regresiva: al llegar a cero se cierra solo.
  useEffect(() => {
    if (!abierto) return;
    const id = setInterval(() => {
      setRestante((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          setAbierto(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [abierto]);

  return (
    <IonModal isOpen={abierto} backdropDismiss={false} cssClass="comunicado-modal">
      <div className="comunicado-fondo" onClick={cerrar}>
        <img className="comunicado-img" src={COMUNICADO_IMG} alt="Comunicado" />
        <button
          type="button"
          className="comunicado-cerrar"
          aria-label="Cerrar comunicado"
          onClick={(e) => {
            e.stopPropagation();
            cerrar();
          }}
        >
          <span className="comunicado-cerrar-x">&times;</span>
          <span className="comunicado-cerrar-seg">{restante}s</span>
        </button>
        <div className="comunicado-ayuda">Tocá la pantalla para continuar</div>
      </div>
    </IonModal>
  );
};

export default ComunicadoModal;
